import { CmsHtml } from '@packages/modules/src/CmsHtml/CmsHtml.client';
import { ComponentTypes } from '../../../interfaces/components';
import { CategoryTeaser } from '../CategoryTeaser/CategoryTeaser.client';
import { CmsAccordion } from '../CmsAccordion/CmsAccordion.client';
import { CmsCarousel } from '../CmsCarousel/CmsCarousel.client';
import { CmsDynamicYield } from '../CmsDynamicYield/CmsDynamicYield';
import { CmsEpsRecommendation } from '../CmsEpsRecommendation/CmsEpsRecommendation';
import { CmsGroup } from '../CmsGroup/CmsGroup';
import { CmsImage } from '../CmsImage/CmsImage.client';
import { CmsInstallmentCalculator } from '../CmsInstallmentCalculator/CmsInstallmentCalculator.client';
import { CmsRecommendation } from '../CmsRecommendation/CmsRecommendation';
import { EmptySlot } from '../EmptySlot/EmptySlot';
import { ExternalScript } from '../ExternalScript/ExternalScript.client';
import { FlagshipTeaser } from '../FlagshipTeaser/FlagshipTeaser.client';
import { BlockBadges } from '../Footer/BlockBadges/BlockBadges';
import { BlockImage } from '../Footer/BlockImage/BlockImage';
import { BlockList } from '../Footer/BlockList/BlockList';
import { BlockText } from '../Footer/BlockText/BlockText';
import { ImageGroup } from '../Footer/ImageGroup/ImageGroup.client';
import {
  LegacyNewsletter,
  NewsletterSubscribe,
  NewsletterUnsubscribe,
} from '../Forms/Newsletter/Newsletter.client';
import { CmsHeadline } from '../Headline/Headline.client';
import { ContentControl } from '../ContentControl/ContentControl';
import { ContentLayout } from '../ContentLayout/ContentLayout';
import { HermesShopFinder } from '../HermesShopFinder/HermesShopFinder';
import { LinkBox } from '../LinkBox/LinkBox.client';
import { LinkByButton } from '../LinkByButton/LinkByButton.client';
import { LinkByText } from '../LinkByText/LinkByText.client';
import { ModalByButton } from '../ModalByButton/ModalByButton.client';
import { ModalByTextlink } from '../ModalByTextlink/ModalByTextlink';
import { OverlayCircle } from '../OverlayCircle/OverlayCircle.client';
import { OverlayRectangle } from '../OverlayRectangle/OverlayRectangle.client';
import { Placeholder } from '../Placeholder/Placeholder.client';
import { RelatedContent } from '../RelatedContent/RelatedContent.client';
import { SectionSeperator } from '../SectionSeperator/SectionSeperator';
import { Video } from '../Video/Video.client';
import { Voucher } from '../Voucher/Voucher.client';
import { registerComponents } from './componentRegistry';

// Guard to ensure components are only registered once
let isRegistered = false;

/**
 * Registers all CMS components directly.
 * Components self-declare their server/client status via 'use client' directive.
 * Uses a guard to ensure registration only happens once.
 */
export const registerFlexibleComponents = (): void => {
  // Early return if already registered
  if (isRegistered) {
    return;
  }

  isRegistered = true;

  registerComponents({
    [ComponentTypes.Group]: CmsGroup,
    [ComponentTypes.GroupLocalizable]: CmsGroup,
    [ComponentTypes.GroupApp]: CmsGroup,
    [ComponentTypes.DynamicYield]: CmsDynamicYield,
    [ComponentTypes.SectionSeperator]: SectionSeperator,
    [ComponentTypes.HermesShopFinder]: HermesShopFinder,
    [ComponentTypes.EmptySlot]: EmptySlot,
    [ComponentTypes.BlockText]: BlockText,
    [ComponentTypes.BlockList]: BlockList,
    [ComponentTypes.BlockImage]: BlockImage,
    [ComponentTypes.BlockBadges]: BlockBadges,
    [ComponentTypes.ContentControl]: ContentControl,
    [ComponentTypes.ContentLayout]: ContentLayout,
    [ComponentTypes.TextlinkModal]: ModalByTextlink,
    [ComponentTypes.RecoPrudsys]: CmsRecommendation,
    [ComponentTypes.RecoEps]: CmsEpsRecommendation,
    [ComponentTypes.Html]: CmsHtml,
    [ComponentTypes.HtmlParagraph]: CmsHtml,
    [ComponentTypes.Placeholder]: Placeholder,
    [ComponentTypes.SvgFile]: CmsImage,
    [ComponentTypes.ImageSimple]: CmsImage,
    [ComponentTypes.ImageFooter]: CmsImage,
    [ComponentTypes.Headline]: CmsHeadline,
    [ComponentTypes.Accordion]: CmsAccordion,
    [ComponentTypes.AccordionLocalizable]: CmsAccordion,
    [ComponentTypes.LinkByButton]: LinkByButton,
    [ComponentTypes.FlagshipTeaser]: FlagshipTeaser,
    [ComponentTypes.LinkByText]: LinkByText,
    [ComponentTypes.LinkBox]: LinkBox,
    [ComponentTypes.RelatedContent]: RelatedContent,
    [ComponentTypes.OverlayCircle]: OverlayCircle,
    [ComponentTypes.OverlayRectangle]: OverlayRectangle,
    [ComponentTypes.InstallmentCalculator]: CmsInstallmentCalculator,
    [ComponentTypes.ButtonModal]: ModalByButton,
    [ComponentTypes.ExternalScript]: ExternalScript,
    [ComponentTypes.ExternalScriptHappyContest]: ExternalScript,
    [ComponentTypes.ExternalScriptLoadbee]: ExternalScript,
    [ComponentTypes.Newsletter]: LegacyNewsletter,
    [ComponentTypes.NewsletterSubscription]: NewsletterSubscribe,
    [ComponentTypes.NewsletterUnsubscribe]: NewsletterUnsubscribe,
    [ComponentTypes.VoucherList]: Voucher,
    [ComponentTypes.Video]: Video,
    [ComponentTypes.Carousel]: CmsCarousel,
    [ComponentTypes.FooterGroupImage]: ImageGroup,
    [ComponentTypes.CategoryTeaser]: CategoryTeaser,
  });
};

/**
 * Resets the registration guard (for testing purposes only)
 * Call this in test setup to allow re-registration
 */
export const resetFlexibleComponentsRegistration = (): void => {
  isRegistered = false;
};
